/**
 * Whether a ringing phone is allowed to reach past the browser tab, and what it
 * says when it does.
 *
 * The card that goes up while the phone rings is inside the SPA, so it is worth
 * exactly as much as the operator's attention on that window. An operator in a
 * spreadsheet, in Slack, or looking at the second monitor gets nothing at all
 * today: the tab title does not change, the icon does not change, and no system
 * notice appears. The call rings out and turns up in the missed list.
 *
 * ⚠ THE GATE IS FOCUS, NOT VISIBILITY, and this is the one line to get right.
 * Two helpers elsewhere in this repo (the admin chat and the site chat widget)
 * suppress their notice unless `document.visibilityState === "hidden"`. A CRM
 * window on a second monitor, or behind Excel on the same screen, is `visible` -
 * so that rule would go quiet in exactly the situation this file exists for.
 * `document.hasFocus()` is the question actually being asked: is the operator
 * looking at this window right now.
 *
 * The decisions live here, pure, so "would a notice be raised" can be answered
 * without a browser to raise one in. `useCallAlert.ts` is the wiring.
 */

/** What the browser will currently allow, folded into one value. */
export type AlertPermission = "unsupported" | "default" | "granted" | "denied";

/** What should happen when the phone starts ringing. */
export type AlertVerdict =
  /** Raise the system notice. */
  | "raise"
  /** The operator is looking at this window; the card is already in front of them. */
  | "focused"
  /** Switched off here, or never switched on. */
  | "off"
  /** The browser refuses, and no amount of asking from inside a ring will help. */
  | "blocked";

export interface AlertVerdictInput {
  permission: AlertPermission;
  /** The operator turned the notices on in this browser. */
  enabled: boolean;
  /** `document.hasFocus()`. NOT `visibilityState` - see the ⚠ above. */
  focused: boolean;
}

export function alertVerdict(input: AlertVerdictInput): AlertVerdict {
  if (!input.enabled) return "off";
  if (input.permission !== "granted") return "blocked";
  // Last, so an operator who is watching the window is told "focused" rather
  // than a permission problem they do not have.
  return input.focused ? "focused" : "raise";
}

/** What pressing the switch should do, given where the permission stands. */
export type ToggleStep =
  /** Turn it off; nothing to ask. */
  | "disable"
  /** Ask the browser for permission, then turn it on if it says yes. */
  | "ask"
  /** Permission is already granted; just turn it on. */
  | "enable"
  /**
   * The operator blocked notices for this site at some point. `requestPermission`
   * resolves "denied" immediately and without a prompt, so asking again produces
   * a switch that flicks back with no explanation. Say so instead.
   */
  | "blocked"
  /** No Notification API at all - iOS Safari, and any browser without one. */
  | "unsupported";

export function toggleStep(next: boolean, permission: AlertPermission): ToggleStep {
  if (permission === "unsupported") return "unsupported";
  if (!next) return "disable";
  if (permission === "denied") return "blocked";
  return permission === "granted" ? "enable" : "ask";
}

/**
 * The tab title while the phone is ringing.
 *
 * The caller in front, because a taskbar entry and a tab strip both truncate
 * from the right, and by the time an operator is reading this they are looking
 * at a strip of twenty tabs. The bell is there for the same reason: at eight
 * characters of visible title it is the only part still legible.
 */
export function ringingTitle(caller: string, base: string): string {
  const who = caller.trim();
  return who ? `${RING_MARK} ${who} - ${base}` : `${RING_MARK} ${base}`;
}

const RING_MARK = "☎";

/**
 * The tab icon while the phone is ringing, as a data URI.
 *
 * Inline rather than a file in `public/`, and that is not laziness: this app has
 * no assets of its own, nginx serves everything there as immutable, and adding
 * one would mean inventing a cache-busting story for a 300-byte icon. A data URI
 * has no cache to bust.
 *
 * Drawn as a filled disc rather than a telephone glyph. At sixteen pixels a
 * handset is an unreadable smudge, while a solid colour change in the tab strip
 * is visible from across a desk - which is the whole job.
 */
export const RINGING_FAVICON =
  "data:image/svg+xml," +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16">' +
      '<circle cx="8" cy="8" r="7" fill="#16a34a"/>' +
      '<circle cx="8" cy="8" r="3" fill="#ffffff"/>' +
      "</svg>",
  );

const KEY = "crm.call.alert";

/**
 * Is the system notice switched on in THIS browser?
 *
 * Device-local, like the audio device choice next door and for the same reason:
 * permission is granted per browser profile, so an answer carried to the
 * operator's second laptop would be an answer about a machine they are not
 * sitting at. Never throws - storage can be switched off.
 */
export function readAlertEnabled(): boolean {
  try {
    return localStorage.getItem(KEY) === "1";
  } catch {
    return false;
  }
}

export function writeAlertEnabled(next: boolean): void {
  try {
    localStorage.setItem(KEY, next ? "1" : "0");
  } catch {
    // Private mode. The switch still holds for this session.
  }
}
